import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import Loader from "../../components/ui/Loader";
import api from "../../api/client";
import { ArrowLeft, FileText, Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const ROWS_PER_PAGE = 10;

export default function ApplicationsPage() {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(true);
 const [applications, setApplications] = useState([]);
 const [filteredApplications, setFilteredApplications] = useState([]);
 const [statusFilter, setStatusFilter] = useState("ALL");
 const [chartFilter, setChartFilter] = useState("7_DAYS");
 const [chartData, setChartData] = useState([]);
 const [currentPage, setCurrentPage] = useState(1);

 const fetchApplications = useCallback(async () => {
 setLoading(true);
 try {
 const res = await api.get("/admin/applications");
 setApplications(res.data.applications || []);
 } catch (err) {
 console.error("Error fetching applications:", err);
 } finally {
 setLoading(false);
 }
 }, []);

 const applyFilters = useCallback(() => {
 let filtered = statusFilter === "ALL" ? applications : applications.filter(a => a.status === statusFilter);

 const now = new Date();
 let startDate;
 switch (chartFilter) {
 case "7_DAYS":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
 break;
 case "MONTH":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
 break;
 case "3_MONTHS":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
 break;
 case "YEAR":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 364);
 break;
 case "ALL":
 default:
 startDate = null;
 }

 if (startDate) {
 filtered = filtered.filter(a => new Date(a.appliedAt) >= startDate);
 }

 setFilteredApplications(filtered);
 setCurrentPage(1);
 generateChart(filtered, startDate);
 }, [applications, statusFilter, chartFilter]);

 const generateChart = (data, startDate) => {
 if (!data.length) {
 setChartData([]);
 return;
 }

 const grouped = {};
 const now = new Date();

 if (startDate) {
 const totalDays = Math.ceil((now - startDate) / (1000 * 60 * 60 * 24)) + 1;
 for (let i = 0; i < totalDays; i++) {
 const date = new Date(startDate);
 date.setDate(startDate.getDate() + i);
 const dayStr = date.toISOString().split("T")[0];
 grouped[dayStr] = 0;
 }
 }

 data.forEach(app => {
 const day = new Date(app.appliedAt).toISOString().split("T")[0];
 grouped[day] = (grouped[day] || 0) + 1;
 });

 const chartArr = Object.keys(grouped)
 .sort()
 .map(day => ({ name: day, applications: grouped[day] }));
 setChartData(chartArr);
 };

 useEffect(() => {
 fetchApplications();
 }, [fetchApplications]);

 useEffect(() => {
 applyFilters();
 }, [applyFilters]);

 const totalPages = Math.ceil(filteredApplications.length / ROWS_PER_PAGE);
 const paginatedData = filteredApplications.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

 if (loading) return <Loader />;

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button 
 variant="outline" 
 onClick={() => navigate("/admin/dashboard")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2.5 rounded-xl bg-gradient-to-r from-primary to-accent shadow-lg">
 <FileText className="w-6 h-6 text-white" />
 </div>
 <h1 className="text-3xl md:text-4xl font-bold">Applications Analytics</h1>
 </div>
 <p className="text-muted-foreground">Total Applications: {filteredApplications.length}</p>
 </div>

 {/* Filters */}
 <Card className="mb-8">
 <CardContent className="p-6">
 <div className="flex flex-wrap gap-4">
 <Select value={chartFilter} onValueChange={setChartFilter}>
 <SelectTrigger className="w-48">
 <SelectValue placeholder="Filter by date range" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="7_DAYS">Last 7 Days</SelectItem>
 <SelectItem value="MONTH">Last Month</SelectItem>
 <SelectItem value="3_MONTHS">Last 3 Months</SelectItem>
 <SelectItem value="YEAR">Last Year</SelectItem>
 <SelectItem value="ALL">Overall</SelectItem>
 </SelectContent>
 </Select>

 <Select value={statusFilter} onValueChange={setStatusFilter}>
 <SelectTrigger className="w-48">
 <SelectValue placeholder="Filter by status" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="ALL">All Status</SelectItem>
 <SelectItem value="PENDING">Pending</SelectItem>
 <SelectItem value="ACCEPTED">Accepted</SelectItem>
 <SelectItem value="REJECTED">Rejected</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </CardContent>
 </Card>

 {/* Chart */}
 <Card className="mb-8">
 <CardHeader>
 <CardTitle>Applications Over Time</CardTitle>
 </CardHeader>
 <CardContent>
 {chartData.length ? (
 <div className="w-full h-80">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={chartData}>
 <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
 <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
 <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
 <Tooltip 
 contentStyle={{
 backgroundColor: "hsl(var(--card))",
 border: "1px solid hsl(var(--border))",
 borderRadius: "8px"
 }}
 />
 <Line type="monotone" dataKey="applications" stroke="hsl(var(--primary))" strokeWidth={2} />
 </LineChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="text-center py-12">
 <p className="text-muted-foreground">No applications to display in the selected period.</p>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Applications List */}
 <Card>
 <CardHeader>
 <CardTitle>Applications List</CardTitle>
 </CardHeader>
 <CardContent>
 {paginatedData.length ? (
 <>
 <div className="space-y-4">
 {paginatedData.map(app => (
 <Card 
 key={app.id} 
 className="hover:shadow-md transition-shadow cursor-pointer"
 onClick={() => navigate(`/admin/applications/${app.id}`)}
 >
 <CardContent className="p-5">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-2">
 <h3 className="font-semibold truncate">
 {app.applierName || app.applier?.applierProfile?.fullName || "Unknown"}
 </h3>
 <Badge variant={
 app.status === 'ACCEPTED' ? 'default' :
 app.status === 'REJECTED' ? 'destructive' :
 'secondary'
 }>
 {app.status}
 </Badge>
 </div>
 <p className="text-sm text-muted-foreground mb-1">
 {app.applierEmail || app.applier?.email}
 </p>
 <p className="text-sm text-muted-foreground">
 Job: {app.jobTitle || app.job?.title || "N/A"}
 </p>
 {(app.university || app.applier?.applierProfile?.university) && (
 <p className="text-xs text-primary mt-1">
 {app.university || app.applier?.applierProfile?.university}
 {app.gpa && ` • GPA: ${app.gpa}`}
 </p>
 )}
 </div>

 {(app.resumeUrl || app.applier?.applierProfile?.resumeUrl) && (
 <Button
 size="sm"
 variant="outline"
 onClick={(e) => {
 e.stopPropagation();
 const rawUrl = app.resumeUrl || app.applier?.applierProfile?.resumeUrl;
 const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, "");
 const normalizedPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
 const fullUrl = rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${normalizedPath}`;
 window.open(fullUrl, '_blank');
 }}
 >
 <Download className="w-4 h-4 mr-2" />
 Resume
 </Button>
 )}
 </div>
 </CardContent>
 </Card>
 ))}
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex justify-center items-center mt-6 gap-4">
 <Button 
 size="sm" 
 variant="outline"
 disabled={currentPage === 1} 
 onClick={() => setCurrentPage(p => p - 1)}
 >
 Previous
 </Button>
 <span className="text-sm font-medium">
 Page {currentPage} of {totalPages}
 </span>
 <Button 
 size="sm" 
 variant="outline"
 disabled={currentPage === totalPages} 
 onClick={() => setCurrentPage(p => p + 1)}
 >
 Next
 </Button>
 </div>
 )}
 </>
 ) : (
 <div className="text-center py-12">
 <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
 <p className="text-muted-foreground">No applications found.</p>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </div>
 );
}
